/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState, useCallback } from 'react';

import { jsx, cssMap, cssMap as unboundCssMap, cx } from '@compiled/react';

import { Box } from '@atlaskit/primitives/compiled';
import {
	ActionName,
	MetadataBlock,
	SmartLinkTheme,
	TitleBlock,
	SnippetBlock,
	ElementName,
} from '@atlaskit/smart-card';
import { Card } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import type { KnowledgeCardSource } from './types';
import { constructSourceUrl } from './utils';

const styles = cssMap({
	container: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.150'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		position: 'relative',
		overflow: 'hidden',
	},
	containerUnresolved: {
		minHeight: '100px',
	},
	skeletonOverlay: {
		position: 'absolute',
		inset: '0',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
	},
});

const unboundStyles = unboundCssMap({
	titleBlockCss: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		"[data-smart-element='Title']": {
			font: token('font.body'),
			fontWeight: token('font.weight.semibold'),
		},
	},
	metadataBlockCss: {
		marginTop: token('space.negative.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		'& span': {
			color: token('color.text.subtlest'),
		},
	},
	snippetBlockCss: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		'& span': {
			color: token('color.text.subtle'),
		},
	},
});

interface SourcesRightRailCardProps {
	index: number;
	onClickHandler: (params: any) => void;
	onError?: (error: any) => void;
	searchSessionId: string;
	source: KnowledgeCardSource;
}

export const SourcesRightRailCard = ({
	source,
	index,
	onClickHandler,
	onError,
	searchSessionId,
}: SourcesRightRailCardProps): React.JSX.Element => {
	const [resolved, setResolved] = useState(false);

	const handleResolve = useCallback(() => {
		setResolved(true);
	}, []);

	const handleClick = (e: React.MouseEvent) => {
		const isAuxClick = e.button === 1;
		onClickHandler?.({
			index,
			source,
			sourceCardType: 'right-rail-source',
			isAuxClick,
		});
		e.preventDefault();
		const url = constructSourceUrl({ source, searchSessionId });
		window.open(url, '_blank', 'noopener, noreferrer');
	};

	return (
		<Box
			xcss={cx(styles.container, !resolved && styles.containerUnresolved)}
			onAuxClick={handleClick}
		>
			<Card
				appearance="block"
				showHoverPreview={false}
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: false,
					hideElevation: true,
					hidePadding: true,
					hideBackground: true,
					removeBlockRestriction: true,
				}}
				url={source.url}
				onClick={handleClick}
				onResolve={handleResolve}
				onError={(error) => {
					setResolved(true);
					onError?.(error);
				}}
			>
				<TitleBlock
					css={unboundStyles.titleBlockCss}
					maxLines={1}
					actions={[
						{ name: ActionName.CopyLinkAction, hideContent: true },
						{ name: ActionName.PreviewAction, hideContent: true },
					]}
					showActionOnHover={true}
				/>
				<MetadataBlock
					css={unboundStyles.metadataBlockCss}
					primary={[
						{ name: ElementName.ModifiedOn },
						{ name: ElementName.ModifiedBy },
						{ name: ElementName.OwnedBy },
					]}
					maxLines={1}
				/>
				<SnippetBlock css={unboundStyles.snippetBlockCss} maxLines={2} />
			</Card>
			{!resolved && <Box xcss={styles.skeletonOverlay} />}
		</Box>
	);
};
